import logger from '@/utils/logger';

import { useEffect, useRef } from 'react';

import { Container, Stack } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { defined } from '@teo/types';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { RouteFallback } from '../components/RouteFallback';
import { AppButton } from '../components/design-system/AppButton';
import { InlineAlert } from '../components/design-system/InlineAlert';
import { useAuth } from '../contexts/AuthContext';
import { rememberWorkosLogoutUrl, storeAuthTokens } from '../lib/authSession';
import { trpc } from '../lib/trpc';

/**
 * Where hosted AuthKit returns the browser. The code in the query string is
 * exchanged by our backend, because the exchange needs the WorkOS API key and
 * that must never reach a bundle.
 *
 * The token stored here is WorkOS's own access token, not one of ours. That is
 * deliberate: it makes `requireAuth` take its WorkOS branch, so the
 * auth-source metric that gates decommissioning the legacy stack counts real
 * sessions rather than a translation we would later have to unpick.
 */
export function AuthkitCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const queryClient = useQueryClient();

  const code = params.get('code');
  const state = params.get('state');
  const authkitError = params.get('error_description') ?? params.get('error');

  const callback = useMutation(
    trpc.auth.authkitCallback.mutationOptions({
      onSuccess: (data) => {
        storeAuthTokens(
          { token: data.token, ...defined({ refreshToken: data.refreshToken }) },
          'authkit-callback',
        );
        setUser(data.user);
        // Learned here as well as on mount: sign-in navigates inside the SPA,
        // so AuthProvider does not remount and its own fetch never runs for
        // the session just created. Without this, the first idle timeout of
        // a fresh session would leave the AuthKit cookie alive.
        void queryClient
          .fetchQuery(trpc.auth.workosLogoutUrl.queryOptions())
          .then((result) => {
            if (result.status === 'enabled') rememberWorkosLogoutUrl(result.url);
          })
          .catch(() => {
            logger.info('Could not learn where to end the WorkOS session');
          });
        // Where they were heading before AuthKit, round-tripped through the
        // signed `state`, or the backend's own choice when the flow carried
        // none. Validated on both sides of that trip, so it is safe to
        // navigate to without re-checking here.
        void navigate(data.returnPath, { replace: true });
      },
    }),
  );

  // An authorization code is single-use, so exchanging it twice fails the
  // second time. In development React mounts effects twice, which is precisely
  // that scenario — hence the guard rather than a bare dependency array.
  const exchanged = useRef(false);
  const { mutate } = callback;
  useEffect(() => {
    if (exchanged.current || !code) return;
    exchanged.current = true;
    // `state` only exists when we built the authorization URL. A password
    // reset — and later an invitation — returns a bare code, and the backend
    // picks the destination for those.
    mutate({ code, ...defined({ state: state ?? undefined }) });
  }, [code, state, mutate]);

  const failure = authkitError ?? (callback.error ? callback.error.message : undefined);

  if (!failure && code) {
    return <RouteFallback />;
  }

  return (
    <Container size="xs" py="xl">
      <Stack>
        <InlineAlert
          intent="danger"
          title="Could not complete sign-in"
          data-testid="authkit-callback-error"
        >
          {failure ?? 'That sign-in link was incomplete. Please start again.'}
        </InlineAlert>
        <AppButton
          variant="light"
          intent="primary"
          onClick={() => void navigate('/login', { replace: true })}
        >
          Back to sign in
        </AppButton>
      </Stack>
    </Container>
  );
}
